

import { Button } from "@a2z/ui";

import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useDialogDismiss } from "@/lib/hooks/useDialogDismiss";
import { SubscriptionPaymentSettleForm } from "./SubscriptionPaymentSettleForm";

type Props = {
  storeId: string;
  triggerVariant?: "text" | "icon" | "menu";
  triggerClassName?: string;
  triggerLabel?: string;
  onOpen?: () => void;
  onSuccess?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

export function StorePaymentModal({
  storeId,
  triggerVariant = "text",
  triggerClassName,
  triggerLabel,
  onOpen,
  onSuccess,
  isOpen: controlledOpen,
  onOpenChange,
  hideTrigger,
}: Props) {
  const t = useTranslations();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  useDialogDismiss(isOpen, () => setOpen(false));

  const label = triggerLabel ?? t(keys.admin.subscriptionPaymentAddAction);
  const triggerClasses =
    triggerVariant === "menu"
      ? "w-full text-left text-xs font-semibold text-[color:var(--text-secondary)]"
      : triggerVariant === "icon"
      ? "group relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--border-light)] text-[color:var(--text-muted)] transition hover:border-[color:var(--primary-300)]"
      : "h-9 rounded-lg px-3 text-xs font-semibold text-[color:var(--text-muted)]";
  const triggerButtonVariant =
    triggerVariant === "text" ? "outline" : "ghost";

  return (
    <>
      {hideTrigger ? null : (
        <Button
          type="button"
          variant={triggerButtonVariant}
          onClick={() => {
            setOpen(true);
            onOpen?.();
          }}
          className={`${triggerClasses}${triggerClassName ? ` ${triggerClassName}` : ""}`}
          aria-label={label}
          title={label}
        >
          {triggerVariant === "icon" ? (
            <>
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
              >
                <rect
                  x="3.5"
                  y="7"
                  width="16.5"
                  height="10.5"
                  rx="2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path d="M3.5 10.5h16.5" stroke="currentColor" strokeWidth="2" />
                <path
                  d="M16.5 3.2v8.6M12.2 7.5h8.6"
                  stroke="#5F5AD1"
                  strokeWidth="3.6"
                  strokeLinecap="round"
                />
                <path
                  d="M16.5 3.2v8.6M12.2 7.5h8.6"
                  stroke="white"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                />
              </svg>
              <span className="pointer-events-none absolute right-0 top-full z-10 mt-2 whitespace-nowrap rounded-md bg-[color:var(--text-primary)] px-2 py-1 text-[13px] font-semibold text-[color:var(--surface)] opacity-0 shadow-[var(--shadow-sm)] transition-opacity group-hover:opacity-100">
                {label}
              </span>
            </>
          ) : (
            label
          )}
        </Button>
      )}
      {isOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 pt-10 pb-[max(2.5rem,env(safe-area-inset-bottom))]"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
        >
          <div
            className="max-h-full w-full max-w-lg overflow-y-auto rounded-2xl bg-[color:var(--surface)] p-6 shadow-[var(--shadow-md)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-[color:var(--text-primary)]">
                  {t(keys.admin.subscriptionPaymentAddTitle)}
                </h2>
                <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                  {t(keys.admin.subscriptionPaymentAddHint)}
                </p>
              </div>
              <Button variant="ghost"
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs font-semibold text-[color:var(--text-muted)]"
              >
                {t(keys.common.cancel)}
              </Button>
            </div>
            <div className="mt-5">
              <SubscriptionPaymentSettleForm
                storeId={storeId}
                compact
                defaultOpen
                hideToggle
                onSuccess={() => {
                  setOpen(false);
                  onSuccess?.();
                }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
